import React, { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationContextType {
  showNotification: (message: string, type: NotificationType, duration?: number) => void;
  hideNotification: () => void;
  notification: {
    open: boolean;
    message: string;
    type: NotificationType;
    duration: number;
  };
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: NotificationType;
    duration: number;
  }>({
    open: false,
    message: '',
    type: 'info',
    duration: 5000, // Default duration: 5 seconds
  });

  // Timer reference for auto-hiding notifications
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Clear the timer when component unmounts
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const showNotification = (message: string, type: NotificationType, duration: number = 5000) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setNotification({
      open: true,
      message,
      type,
      duration,
    });

    // Set a timer to auto-hide the notification if duration > 0
    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        hideNotification();
      }, duration);
    }
  };

  const hideNotification = () => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <NotificationContext.Provider
      value={{
        notification,
        showNotification,
        hideNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}; 